<template>
  <Modal
    v-model:visible="isVisible"
    class="manifest-success-modal"
    size="lg"
    no-scroll
    @keypress.esc="isVisible = false"
  >
    <template #header>
      <FontAwesomeIcon aria-hidden="true" :icon="['fal', 'check-circle']" />
      <h1>{{ t('common.success') }}</h1>
    </template>

    <p>
      {{
        t('messages.successCreatedParam', {
          param: t('manifests.manifests', 1),
        })
      }}
    </p>

    <KeyValuePair
      class="manifest-number"
      :label="t('manifests.manifestNumber')"
      :value="result.manifestNumber"
      inline
    />

    <h2 class="text-gray-darker uppercase">
      {{ t('shipments.shipments', totalRecords) }}
    </h2>

    <!--
      Fixed scroll-height (not the usual "flex") because this is a no-scroll modal: a fixed body height
      bounds the table so the paginator and footer stay pinned/visible at larger page sizes.
    -->
    <DataTable
      :value="rows"
      :loading="results.loading"
      :total-records="totalRecords"
      :first="first"
      :rows="pageSize"
      :rows-per-page-options="[10, 20, 50]"
      lazy
      paginator
      paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      data-key="id"
      scrollable
      scroll-height="24rem"
      class="manifest-success-shipments"
      @page="onPage"
    >
      <Column :header="t('shipments.shipments', 1)">
        <template #body="{ data }">
          <router-link
            :to="{ name: 'shipmentDetail', params: { shipmentId: data.id } }"
            @click="isVisible = false"
          >
            {{ t('shipments.shipmentNumParam', { param: data.id }) }}
          </router-link>
        </template>
      </Column>
      <Column :header="CARRIER_COLUMN.header">
        <template #body="{ data, index }">
          <component :is="CARRIER_COLUMN.body" :data="data" :index="index" />
        </template>
      </Column>
      <Column :header="t('referenceData.referenceNumbers', 2)">
        <template #body="{ data }">
          {{ referenceNumbers(data) }}
        </template>
      </Column>
      <Column :header="ORIGIN_COLUMN.header">
        <template #body="{ data, index }">
          <component :is="ORIGIN_COLUMN.body" :data="data" :index="index" />
        </template>
      </Column>
      <Column :header="DESTINATION_COLUMN.header">
        <template #body="{ data, index }">
          <component
            :is="DESTINATION_COLUMN.body"
            :data="data"
            :index="index"
          />
        </template>
      </Column>
      <Column :header="WEIGHT_COLUMN.header">
        <template #body="{ data, index }">
          <component :is="WEIGHT_COLUMN.body" :data="data" :index="index" />
        </template>
      </Column>
      <Column :header="PRODUCT_DESCRIPTION_COLUMN.header">
        <template #body="{ data, index }">
          <component
            :is="PRODUCT_DESCRIPTION_COLUMN.body"
            :data="data"
            :index="index"
          />
        </template>
      </Column>
    </DataTable>

    <template #footer>
      <RygenButton
        v-if="result.manifestNumber"
        v-tooltip.top="{
          value: t('manifests.viewShipmentsRequiresNewSearch'),
          disabled: isElasticsearchEnabled,
        }"
        as="router-link"
        :to="searchRoute"
        :disabled="!isElasticsearchEnabled"
        mode="secondary"
        :label="t('common.viewParam', { param: t('shipments.shipments', 2) })"
        @click="isVisible = false"
      />

      <RygenButton
        v-if="result.manifestId"
        as="router-link"
        :to="{
          name: 'manifestDetail',
          params: { manifestId: result.manifestId },
        }"
        mode="secondary"
        :label="t('common.viewParam', { param: t('manifests.manifests', 1) })"
        @click="isVisible = false"
      />

      <RygenButton
        mode="primary"
        icon="pi pi-check"
        :label="t('common.done')"
        @click="isVisible = false"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast, useTooltip } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useElasticsearch } from '@/composables';
import {
  CARRIER_COLUMN,
  DESTINATION_COLUMN,
  ManifestResponse,
  ORIGIN_COLUMN,
  PRODUCT_DESCRIPTION_COLUMN,
  ShipmentSearchData,
  ShipmentSearchResult,
  WEIGHT_COLUMN,
} from '@/model';
import { buildSearchRoute } from '@/util';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Column from 'primevue/column';
import DataTable, { DataTablePageEvent } from 'primevue/datatable';

const PAGE_SIZE = 10;

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  result: {
    type: Object as PropType<ManifestResponse>,
    required: true,
  },
});
const { result } = toRefs(props);

const { t } = useI18n();
const toast = useToast();
const vTooltip = useTooltip();
// "View Shipments" filters the list by manifest number, which only the Elasticsearch search path supports;
// the legacy DB search has no manifest predicate, so the button is disabled (with an explanatory tooltip)
// rather than routing to a silently unfiltered list.
const { isElasticsearchEnabled } = useElasticsearch({ searchType: 'shipment' });

const results: Ref<Loadable<ShipmentSearchResult>> = ref({ loading: false });
const page = ref(0);
const pageSize = ref(PAGE_SIZE);

const rows = computed(() => results.value.data?.content ?? []);
const totalRecords = computed(() => results.value.data?.totalElements ?? 0);
const first = computed(() => page.value * pageSize.value);

// Opens the shipment list filtered to this manifest's shipments (by manifest number, so the URL stays small
// regardless of how many shipments are on the manifest).
const searchRoute = computed(() =>
  buildSearchRoute('shipmentList', {
    manifestNumber: result.value.manifestNumber,
  }),
);

const referenceNumbers = (shipment: ShipmentSearchData) =>
  shipment.referenceNumbers?.map((refNum) => refNum.value).join(', ') || '-';

const loadShipments = async () => {
  if (result.value.manifestId == null) {
    return;
  }

  try {
    results.value.loading = true;
    results.value.data = await api.shipmentManifest.getManifestShipments(
      result.value.manifestId,
      { page: page.value, pageSize: pageSize.value },
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    results.value.error = e;
    console.error('Error loading manifest shipments', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('shipments.shipments', 2) }),
      error: e,
    });
  } finally {
    results.value.loading = false;
  }
};

const onPage = (event: DataTablePageEvent) => {
  page.value = event.page as number;
  pageSize.value = event.rows as number;
  loadShipments();
};

watch(isVisible, (visible: boolean) => {
  if (visible) {
    page.value = 0;
    pageSize.value = PAGE_SIZE;
    results.value = { loading: false };
    loadShipments();
  }
});
</script>

<style>
.manifest-success-modal.p-dialog {
  .p-dialog-header {
    .modal-header {
      gap: var(--sp-2);
      align-items: center;
      font-size: 18px;

      svg {
        color: var(--success-color);
      }

      h1 {
        color: var(--brand-dark-color);
      }
    }
  }

  .p-dialog-content {
    padding-block: 0;

    p {
      color: var(--brand-dark-color);
    }

    h2 {
      margin: var(--sp-6) 0 var(--sp-3) 0;
      font-size: 12px;
      font-weight: 500;
    }
  }
}

/* Keep the label and value together instead of the default inline space-between spread. */
.manifest-success-modal .manifest-number.key-value-pair-inline {
  justify-content: flex-start;
  gap: var(--sp-2);
}

.manifest-success-modal
  .manifest-number.key-value-pair-inline
  .key-value-pair-label {
  flex: 0 0 auto;
}
</style>
